/**
 * DistanceCalculator Module
 * Calculates distance and time matrices between coordinates using Google Maps Distance Matrix API
 * Includes traffic conditions for accurate time estimates
 */

// In-memory cache for distance matrix results (cleared on page reload)
const distanceCache = new Map();

/**
 * Generates a cache key from coordinates
 * @param {Array<{lat: number, lng: number}>} coordinates - Array of coordinates
 * @returns {string} - Cache key
 */
function generateCacheKey(coordinates) {
  return coordinates
    .map(coord => `${coord.lat.toFixed(6)},${coord.lng.toFixed(6)}`)
    .join('|');
}

/**
 * Calls the Google Maps Distance Matrix API via Cloudflare Worker proxy
 * @param {Array<{lat: number, lng: number}>} coordinates - Array of all locations (including start)
 * @returns {Promise<{distanceMatrix: number[][], timeMatrix: number[][]}>} - Distance (km) and time (min) matrices
 */
async function fetchDistanceMatrix(coordinates) {
  // The Cloudflare Worker will proxy this request to Google Maps API
  const url = '/api/distance-matrix';

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        origins: coordinates,
        destinations: coordinates,
        mode: 'driving',
        departure_time: 'now', // Get current traffic conditions
        units: 'metric'
      })
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (data.status !== 'OK') {
      throw new Error(`Distance Matrix API error: ${data.status} - ${data.error_message || 'Unknown error'}`);
    }

    return parseDistanceMatrixResponse(data);
  } catch (error) {
    throw new Error(`Failed to fetch distance matrix: ${error.message}`);
  }
}

/**
 * Parses Google Maps Distance Matrix API response into our format
 * @param {Object} apiResponse - Response from Google Maps API
 * @returns {{distanceMatrix: number[][], timeMatrix: number[][]}} - Parsed matrices
 */
function parseDistanceMatrixResponse(apiResponse) {
  const rows = apiResponse.rows;
  const n = rows.length;

  const distanceMatrix = Array(n).fill(0).map(() => Array(n).fill(0));
  const timeMatrix = Array(n).fill(0).map(() => Array(n).fill(0));

  for (let i = 0; i < n; i++) {
    const elements = rows[i].elements;

    for (let j = 0; j < n; j++) {
      const element = elements[j];

      if (element.status === 'OK') {
        // Distance in kilometers
        distanceMatrix[i][j] = element.distance.value / 1000;
        // Duration in minutes (with traffic if available)
        const duration = element.duration_in_traffic || element.duration;
        timeMatrix[i][j] = duration.value / 60;
      } else if (i === j) {
        // Distance from a point to itself is 0
        distanceMatrix[i][j] = 0;
        timeMatrix[i][j] = 0;
      } else {
        // If route not found, use a large number (but not Infinity to avoid JSON issues)
        distanceMatrix[i][j] = 999999;
        timeMatrix[i][j] = 999999;
      }
    }
  }

  return { distanceMatrix, timeMatrix };
}

/**
 * Creates a simple estimated distance matrix using Haversine formula
 * Used as fallback when Google Maps API is unavailable
 * @param {Array<{lat: number, lng: number}>} coordinates - Array of coordinates
 * @returns {{distanceMatrix: number[][], timeMatrix: number[][]}} - Estimated matrices
 */
function createEstimatedMatrix(coordinates) {
  const n = coordinates.length;
  const distanceMatrix = Array(n).fill(0).map(() => Array(n).fill(0));
  const timeMatrix = Array(n).fill(0).map(() => Array(n).fill(0));

  // Earth's radius in kilometers
  const R = 6371;

  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if (i === j) {
        distanceMatrix[i][j] = 0;
        timeMatrix[i][j] = 0;
      } else {
        // Haversine formula for distance
        const lat1 = coordinates[i].lat * Math.PI / 180;
        const lat2 = coordinates[j].lat * Math.PI / 180;
        const deltaLat = (coordinates[j].lat - coordinates[i].lat) * Math.PI / 180;
        const deltaLng = (coordinates[j].lng - coordinates[i].lng) * Math.PI / 180;

        const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
                  Math.cos(lat1) * Math.cos(lat2) *
                  Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;

        distanceMatrix[i][j] = distance;
        // Estimate time assuming average speed of 40 km/h in urban areas
        timeMatrix[i][j] = (distance / 40) * 60; // minutes
      }
    }
  }

  return { distanceMatrix, timeMatrix };
}

/**
 * Main function to calculate distance and time matrices
 * Attempts to use Google Maps API, falls back to Haversine estimation
 * Results are cached to avoid redundant API calls
 * @param {Array<{lat: number, lng: number}>} coordinates - Array of all locations (start + stops)
 * @param {boolean} useCache - Whether to use cached results (default: true)
 * @returns {Promise<{distanceMatrix: number[][], timeMatrix: number[][]}>} - Distance and time matrices
 */
export async function calculateMatrix(coordinates, useCache = true) {
  // Validate input
  if (!coordinates || coordinates.length < 2) {
    throw new Error('At least 2 coordinates required for distance matrix calculation');
  }

  // Check cache first
  const cacheKey = generateCacheKey(coordinates);
  if (useCache && distanceCache.has(cacheKey)) {
    return distanceCache.get(cacheKey);
  }

  try {
    // Try to fetch real distances from Google Maps API
    const result = await fetchDistanceMatrix(coordinates);

    // Cache the result
    if (useCache) {
      distanceCache.set(cacheKey, result);
    }

    return result;
  } catch (error) {
    console.warn('Google Maps API unavailable, using estimated distances:', error.message);

    // Fallback to Haversine estimation
    const result = createEstimatedMatrix(coordinates);

    // Cache the fallback result (but don't overwrite a real result if it exists)
    if (useCache && !distanceCache.has(cacheKey)) {
      distanceCache.set(cacheKey, result);
    }

    return result;
  }
}

/**
 * Clears the distance matrix cache
 * Useful for testing or when coordinates change
 */
export function clearCache() {
  distanceCache.clear();
}

/**
 * Gets cache statistics (for debugging)
 * @returns {{size: number, keys: string[]}} - Cache info
 */
export function getCacheStats() {
  return {
    size: distanceCache.size,
    keys: Array.from(distanceCache.keys())
  };
}
